import { Injectable, inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';

import { I18nService } from '@gruposaltaedu/liv-i18n/core';
import { AppBootstrapLoadingService } from 'src/app/core/layout/services/app-bootstrap-loading.service';

import { AppErrorCode } from '../enums/app-error-code.enum';

const GRANT_STORAGE_KEY = 'liv.family-portfolio.appError.grant';
const GRANT_TTL_MS = 15 * 60 * 1000;

interface AppErrorGrant {
  code: AppErrorCode;
  grantedAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class AppErrorNavigationService {
  private readonly router = inject(Router);
  private readonly i18n = inject(I18nService);
  private readonly bootstrapLoading = inject(AppBootstrapLoadingService);
  private memoryGrant: AppErrorGrant | null = null;

  createUrlTree(code: AppErrorCode): UrlTree {
    this.clearBootstrapLoading();
    this.grantAccess(code);
    const lang = this.i18n.currentLanguage();
    return this.router.createUrlTree(['/', lang, 'error', code]);
  }

  createNotFoundUrlTree(): UrlTree {
    this.clearBootstrapLoading();
    const lang = this.i18n.currentLanguage();
    return this.router.createUrlTree(['/', lang, '404']);
  }

  navigate(code: AppErrorCode, replaceUrl = true): Promise<boolean> {
    return this.router.navigateByUrl(this.createUrlTree(code), {
      replaceUrl
    });
  }

  navigateToNotFound(replaceUrl = true): Promise<boolean> {
    return this.router.navigateByUrl(this.createNotFoundUrlTree(), {
      replaceUrl
    });
  }

  private clearBootstrapLoading(): void {
    this.bootstrapLoading.hide();
  }

  canAccess(code: AppErrorCode): boolean {
    const grant = this.readGrant();
    if (!grant) {
      return false;
    }

    if (grant.code !== code) {
      return false;
    }

    if (Date.now() - grant.grantedAt > GRANT_TTL_MS) {
      this.clearGrant();
      return false;
    }

    return true;
  }

  clearGrant(): void {
    this.memoryGrant = null;
    try {
      sessionStorage.removeItem(GRANT_STORAGE_KEY);
    } catch {
      // ignore storage failures
    }
  }

  private grantAccess(code: AppErrorCode): void {
    const grant: AppErrorGrant = { code, grantedAt: Date.now() };
    this.memoryGrant = grant;

    try {
      sessionStorage.setItem(GRANT_STORAGE_KEY, JSON.stringify(grant));
    } catch {
      // memory grant still allows in-app navigation
    }
  }

  private readGrant(): AppErrorGrant | null {
    if (this.memoryGrant) {
      return this.memoryGrant;
    }

    try {
      const raw = sessionStorage.getItem(GRANT_STORAGE_KEY);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as AppErrorGrant;
      if (!parsed?.code || !parsed?.grantedAt) {
        return null;
      }

      this.memoryGrant = parsed;
      return parsed;
    } catch {
      return null;
    }
  }
}
